/**
 * Channels Service
 * API calls for channel operations
 */

import apiClient from './api.client';
import type { ApiResponse, Channel, ChannelMember } from '@/types/api.types';

/**
 * List all channels the current user is a member of
 */
export const listChannels = async (): Promise<Channel[]> => {
  const response = await apiClient.get<ApiResponse<{ channels: Channel[] }>>('/channels');

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch channels');
  }

  return response.data.data.channels;
};

/**
 * Get a single channel by ID
 */
export const getChannel = async (channelId: string): Promise<Channel> => {
  const response = await apiClient.get<ApiResponse<{ channel: Channel }>>(
    `/channels/${channelId}`
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch channel');
  }

  return response.data.data.channel;
};

/**
 * Create a new channel
 */
export const createChannel = async (data: {
  name: string;
  description?: string;
  type: 'public' | 'private' | 'announcement';
}): Promise<Channel> => {
  const response = await apiClient.post<ApiResponse<{ channel: Channel }>>(
    '/channels',
    data
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to create channel');
  }

  return response.data.data.channel;
};

/**
 * Update a channel
 */
export const updateChannel = async (
  channelId: string,
  data: {
    name?: string;
    description?: string;
    type?: 'public' | 'private' | 'announcement';
    position?: number;
  }
): Promise<Channel> => {
  const response = await apiClient.patch<ApiResponse<{ channel: Channel }>>(
    `/channels/${channelId}`,
    data
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to update channel');
  }

  return response.data.data.channel;
};

/**
 * Delete a channel
 */
export const deleteChannel = async (channelId: string): Promise<void> => {
  const response = await apiClient.delete<ApiResponse>(`/channels/${channelId}`);

  if (response.data.status !== 'success') {
    throw new Error(response.data.error || 'Failed to delete channel');
  }
};

/**
 * Archive a channel
 */
export const archiveChannel = async (channelId: string): Promise<Channel> => {
  const response = await apiClient.post<ApiResponse<{ channel: Channel }>>(
    `/channels/${channelId}/archive`
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to archive channel');
  }

  return response.data.data.channel;
};

/**
 * Unarchive a channel
 */
export const unarchiveChannel = async (channelId: string): Promise<Channel> => {
  const response = await apiClient.post<ApiResponse<{ channel: Channel }>>(
    `/channels/${channelId}/restore`
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to unarchive channel');
  }

  return response.data.data.channel;
};

/**
 * Join a channel
 */
export const joinChannel = async (channelId: string): Promise<void> => {
  const response = await apiClient.post<ApiResponse>(`/channels/${channelId}/join`);

  if (response.data.status !== 'success') {
    throw new Error(response.data.error || 'Failed to join channel');
  }
};

/**
 * Leave a channel
 */
export const leaveChannel = async (channelId: string): Promise<void> => {
  const response = await apiClient.post<ApiResponse>(`/channels/${channelId}/leave`);

  if (response.data.status !== 'success') {
    throw new Error(response.data.error || 'Failed to leave channel');
  }
};

/**
 * Get channel members
 */
export const getChannelMembers = async (channelId: string): Promise<ChannelMember[]> => {
  const response = await apiClient.get<ApiResponse<{ members: ChannelMember[] }>>(
    `/channels/${channelId}/members`
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch members');
  }

  return response.data.data.members;
};

/**
 * Add member to channel
 */
export const addChannelMember = async (
  channelId: string,
  userId: string,
  role: 'member' | 'admin' = 'member'
): Promise<void> => {
  const response = await apiClient.post<ApiResponse>(
    `/channels/${channelId}/members`,
    { userId, role }
  );

  if (response.data.status !== 'success') {
    throw new Error(response.data.error || 'Failed to add member');
  }
};

/**
 * Remove member from channel
 */
export const removeChannelMember = async (
  channelId: string,
  userId: string
): Promise<void> => {
  const response = await apiClient.delete<ApiResponse>(
    `/channels/${channelId}/members/${userId}`
  );

  if (response.data.status !== 'success') {
    throw new Error(response.data.error || 'Failed to remove member');
  }
};

/**
 * Mark channel as read (updates last_read_at)
 */
export const markChannelAsRead = async (channelId: string): Promise<void> => {
  const response = await apiClient.post<ApiResponse>(
    `/channels/${channelId}/read`
  );

  if (response.data.status !== 'success') {
    throw new Error(response.data.error || 'Failed to mark channel as read');
  }
};

/**
 * Get channel notification level for current user
 */
export const getChannelNotificationLevel = async (
  channelId: string
): Promise<'all' | 'mentions' | 'none'> => {
  const response = await apiClient.get<ApiResponse<{ notificationLevel: string }>>(
    `/channels/${channelId}/notification-level`
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to get notification level');
  }

  return response.data.data.notificationLevel as 'all' | 'mentions' | 'none';
};

/**
 * Update channel notification level for current user
 */
export const updateChannelNotificationLevel = async (
  channelId: string,
  notificationLevel: 'all' | 'mentions' | 'none'
): Promise<void> => {
  const response = await apiClient.patch<ApiResponse>(
    `/channels/${channelId}/notification-level`,
    { notificationLevel }
  );

  if (response.data.status !== 'success') {
    throw new Error(response.data.error || 'Failed to update notification level');
  }
};

/**
 * Update channel positions (sort order)
 */
export const updateChannelPositions = async (
  positions: { id: string; position: number }[]
): Promise<void> => {
  const response = await apiClient.patch<ApiResponse>(
    '/channels/positions',
    { positions }
  );

  if (response.data.status !== 'success') {
    throw new Error(response.data.error || 'Failed to update channel positions');
  }
};

/**
 * Set default channel
 */
export const setDefaultChannel = async (channelId: string): Promise<void> => {
  const response = await apiClient.patch<ApiResponse>(
    '/channels/default',
    { channelId }
  );

  if (response.data.status !== 'success') {
    throw new Error(response.data.error || 'Failed to set default channel');
  }
};
